// Utils/DepositSummaryIslandExcel.js
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const generateDepositSummaryIslandExcel = async (data, reportLevel = "report", selectedDate = null) => {
  try {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error("No data available to generate Excel");
    }

    const dateLabel =
      selectedDate && typeof selectedDate.format === "function"
        ? selectedDate.format("YYYY-MM-DD")
        : new Date().toISOString().slice(0, 10);

    const normalized = data.map((item) => {
      const locationName = (item.locationName || "").toString().trim();
      const productName = (item.productName || "").toString().trim();

      let depositCount = 0;
      if (item.depositCount !== undefined && item.depositCount !== null && item.depositCount !== "") {
        const c = Number(item.depositCount);
        depositCount = Number.isFinite(c) ? Math.trunc(c) : 0;
      }

      let totalAmount = 0;
      if (item.totalAmount !== undefined && item.totalAmount !== null && item.totalAmount !== "") {
        const a = Number(item.totalAmount);
        totalAmount = Number.isFinite(a) ? a : 0;
      }

      return {
        ...item,
        locationName,
        productName,
        depositCount,
        totalAmount,
      };
    });

    const grouped = normalized.reduce((acc, cur) => {
      const loc = cur.locationName || "UNSPECIFIED";
      if (!acc[loc]) acc[loc] = [];
      acc[loc].push(cur);
      return acc;
    }, {});

    const locations = Object.keys(grouped).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    const productMapping = {
      "MEMBER DEPOSIT": "Member Deposits",
      "CHILD DEPOSIT": "Child deposits",
      "DIRIYAMATHA  ": "Diriyamatha deposits",
      "DIRIYAMATHA": "Diriyamatha deposits",
      "NON MEMBER DEPOSIT": "Non members deposits",
      "GROUPS DEPOSIT": "Group deposits",
      "SISURAKA": "Sisuraka",
      "COMPULSORY SAVINGS": "Compulsory savings",
      "SHARES": "Shares",
      "FIXED DEPOSIT": "FIX Deposits",
      "DHANASHAKTHI INVEST SAVING ACCOUNT":
        "Dhanashakthi Invest Saving Account",
    };

    Object.keys(grouped).forEach(loc => {
      grouped[loc] = grouped[loc].map(item => {
        const normalizedProductName = item.productName.toUpperCase().trim();
        const mappedName = productMapping[normalizedProductName] || item.productName;
        return {
          ...item,
          productName: mappedName,
        };
      });
    });

    const productStructure = [
      { name: "Member Deposits", type: "product" },
      { name: "Child deposits", type: "product" },
      { name: "Diriyamatha deposits", type: "product" },
      { name: "Non members deposits", type: "product" },
      { name: "Group deposits", type: "product" },
      { name: "Sisuraka", type: "product" },
      { name: "Sub Total", type: "subtotal", sumRange: [0, 5] },
      { name: "Compulsory savings", type: "product" },
      { name: "Sub Total", type: "subtotal", sumRange: [6, 7] },
      { name: "Shares", type: "product" },
      { name: "FIX Deposits", type: "product" },
      { name: "Dhanashakthi Invest Saving Account", type: "product" },

      // UPDATE TOTAL RANGE
      { name: "Total", type: "total", sumRange: [8, 11] },
    ];

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Deposit Summary", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 4 }],
    });

    const totalColumns = 1 + locations.length * 2;
    const minColumnsForTitle = Math.max(totalColumns, 8);

    ws.mergeCells(1, 1, 1, minColumnsForTitle);
    const titleCell = ws.getCell(1, 1);
    titleCell.value = `Samurdhi Banks ${reportLevel} & Island Deposits Summary (${dateLabel})`;
    titleCell.font = { bold: true, size: 16 };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    ws.getRow(1).height = 24;

    ws.getRow(2).height = 6;

    ws.getCell(3, 1).value = "Deposits";
    ws.getCell(3, 1).font = { bold: true };
    ws.getCell(3, 1).alignment = { vertical: "middle", horizontal: "left" };
    let col = 2;
    locations.forEach((loc) => {
      ws.mergeCells(3, col, 3, col + 1);
      const c = ws.getCell(3, col);
      c.value = loc;
      c.font = { bold: true, size: 14 };
      c.alignment = { horizontal: "center" };
      ws.getCell(4, col).value = "No of A/C";
      ws.getCell(4, col + 1).value = "Amount";
      ws.getCell(4, col).font = { bold: true };
      ws.getCell(4, col + 1).font = { bold: true };
      ws.getCell(4, col).alignment = { horizontal: "center" };
      ws.getCell(4, col + 1).alignment = { horizontal: "center" };
      col += 2;
    });

    const columnsDefinition = [{ width: 40 }];
    for (let i = 0; i < locations.length; i++) {
      columnsDefinition.push({ width: 14 });
      columnsDefinition.push({ width: 20 });
    }
    ws.columns = columnsDefinition;

    let rowIndex = 5;
    const rowMapping = {};

    productStructure.forEach((item, idx) => {
      const isSubtotalOrTotal = item.type === "subtotal" || item.type === "total";

      ws.getCell(rowIndex, 1).value = item.name;
      ws.getCell(rowIndex, 1).alignment = { vertical: "top", horizontal: "left" };

      if (isSubtotalOrTotal) {
        ws.getCell(rowIndex, 1).font = { bold: true };
      }

      rowMapping[idx] = rowIndex;

      if (item.type === "product") {
        let pos = 2;
        locations.forEach((loc) => {
          const found = grouped[loc].find((d) => d.productName === item.name);

          const countVal = found ? found.depositCount || 0 : 0;
          ws.getCell(rowIndex, pos).value = Number(countVal);
          ws.getCell(rowIndex, pos).numFmt = "#,##0";
          ws.getCell(rowIndex, pos).alignment = { horizontal: "right" };

          const amountVal = found ? found.totalAmount || 0 : 0;
          ws.getCell(rowIndex, pos + 1).value = Number(amountVal);
          ws.getCell(rowIndex, pos + 1).numFmt = "#,##0.00";
          ws.getCell(rowIndex, pos + 1).alignment = { horizontal: "right" };

          pos += 2;
        });
      } else if (isSubtotalOrTotal) {
        let pos = 2;

        const startRow = rowMapping[item.sumRange[0]];
        const endRow = rowMapping[item.sumRange[item.sumRange.length - 1]];

        locations.forEach(() => {
          const countStartAddr = ws.getCell(startRow, pos)._address;
          const countEndAddr = ws.getCell(endRow, pos)._address;

          ws.getCell(rowIndex, pos).value = {
            formula: `SUM(${countStartAddr}:${countEndAddr})`,
          };
          ws.getCell(rowIndex, pos).numFmt = "#,##0";
          ws.getCell(rowIndex, pos).font = { bold: true };
          ws.getCell(rowIndex, pos).alignment = { horizontal: "right" };

          const amountStartAddr = ws.getCell(startRow, pos + 1)._address;
          const amountEndAddr = ws.getCell(endRow, pos + 1)._address;

          ws.getCell(rowIndex, pos + 1).value = {
            formula: `SUM(${amountStartAddr}:${amountEndAddr})`,
          };
          ws.getCell(rowIndex, pos + 1).numFmt = "#,##0.00";
          ws.getCell(rowIndex, pos + 1).font = { bold: true };
          ws.getCell(rowIndex, pos + 1).alignment = { horizontal: "right" };

          pos += 2;
        });
      }

      rowIndex++;
    });

    const lastRow = rowIndex - 1;
    const borderOptions = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    for (let r = 3; r <= lastRow; r++) {
      for (let c = 1; c <= totalColumns; c++) {
        const cell = ws.getCell(r, c);
        cell.border = borderOptions;
      }
    }

    const summary = workbook.addWorksheet("Summary", {
      views: [{ state: "frozen", xSplit: 1, ySplit: 4 }],
    });

    summary.mergeCells(1, 1, 1, minColumnsForTitle);
    const summaryTitleCell = summary.getCell(1, 1);
    summaryTitleCell.value = `Samurdhi Banks ${reportLevel} & Island Deposits Summary (${dateLabel})`;
    summaryTitleCell.font = { bold: true, size: 16 };
    summaryTitleCell.alignment = { horizontal: "center", vertical: "middle" };
    summary.getRow(1).height = 24;

    summary.getRow(2).height = 6;

    summary.getCell(3, 1).value = "Deposits";
    summary.getCell(3, 1).font = { bold: true };
    summary.getCell(3, 1).alignment = { vertical: "middle", horizontal: "left" };
    let summaryCol = 2;
    locations.forEach((loc) => {
      summary.mergeCells(3, summaryCol, 3, summaryCol + 1);
      const c = summary.getCell(3, summaryCol);
      c.value = loc;
      c.font = { bold: true, size: 14 };
      c.alignment = { horizontal: "center" };
      summary.getCell(4, summaryCol).value = "No of A/C";
      summary.getCell(4, summaryCol + 1).value = "Amount";
      summary.getCell(4, summaryCol).font = { bold: true };
      summary.getCell(4, summaryCol + 1).font = { bold: true };
      summary.getCell(4, summaryCol).alignment = { horizontal: "center" };
      summary.getCell(4, summaryCol + 1).alignment = { horizontal: "center" };
      summaryCol += 2;
    });

    const summaryColumns = [{ width: 40 }];
    for (let i = 0; i < locations.length; i++) {
      summaryColumns.push({ width: 14 });
      summaryColumns.push({ width: 20 });
    }
    summary.columns = summaryColumns;

    const savingsProductNames = [
      "Member Deposits",
      "Child deposits",
      "Diriyamatha deposits",
      "Non members deposits",
      "Group deposits",
      "Sisuraka",
      "Compulsory savings",
    ];
    const allProductNames = productStructure
      .filter((item) => item.type === "product")
      .map((item) => item.name);

    const sumByLocation = (loc, productNames) =>
      grouped[loc]
        .filter((d) => productNames.includes(d.productName))
        .reduce(
          (acc, d) => ({
            count: acc.count + d.depositCount,
            amount: acc.amount + d.totalAmount,
          }),
          { count: 0, amount: 0 }
        );

    const writeSummaryRow = (rowIdx, label, productNames) => {
      summary.getCell(rowIdx, 1).value = label;
      summary.getCell(rowIdx, 1).font = { bold: true };
      summary.getCell(rowIdx, 1).alignment = { vertical: "middle", horizontal: "left" };

      let pos = 2;
      locations.forEach((loc) => {
        const totals = sumByLocation(loc, productNames);

        summary.getCell(rowIdx, pos).value = totals.count;
        summary.getCell(rowIdx, pos).numFmt = "#,##0";
        summary.getCell(rowIdx, pos).font = { bold: true };
        summary.getCell(rowIdx, pos).alignment = { horizontal: "right" };

        summary.getCell(rowIdx, pos + 1).value = totals.amount;
        summary.getCell(rowIdx, pos + 1).numFmt = "#,##0.00";
        summary.getCell(rowIdx, pos + 1).font = { bold: true };
        summary.getCell(rowIdx, pos + 1).alignment = { horizontal: "right" };

        pos += 2;
      });
    };

    writeSummaryRow(5, "Sub Total", savingsProductNames);
    writeSummaryRow(6, "Total", allProductNames);

    for (let r = 3; r <= 6; r++) {
      for (let c = 1; c <= totalColumns; c++) {
        summary.getCell(r, c).border = borderOptions;
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const filename = `Deposit_Summary_${reportLevel || "report"}_${dateLabel}.xlsx`;

    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      filename
    );

    return true;
  } catch (err) {
    console.error("generateDepositSummaryIslandExcel error:", err);
    throw err;
  }
};

export default generateDepositSummaryIslandExcel;